import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Section } from "@/components/section";
import { putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import type { App } from "@/types/domain";
import { refreshApps } from "./refresh-apps";

// Per-app tab switches: many apps never use channels/RC etc., so the owner
// can drop those tabs here. Mirrors the server's HIDEABLE_TABS roster —
// core navigation (Prds/Versions/Settings) isn't offered. Ticked = shown;
// all ticked saves as null (unconfigured = everything on, the legacy
// default). UI-only: direct URLs keep working, permissions unchanged.
const TAB_OPTIONS = [
  { key: "docs", label: "Docs" },
  { key: "reports", label: "Reports" },
  { key: "rollout", label: "Rollout" },
  { key: "channels", label: "Channels" },
  { key: "artifacts", label: "Artifacts" },
  { key: "rcs", label: "RC" },
];

const norm = (keys: string[]) => [...new Set(keys)].sort().join(",");

export const AppTabsSection = ({ app }: { app: App }) => {
  const { toast } = useToast();
  const savedHidden = app.hiddenTabs ?? [];
  const [hidden, setHidden] = useState<string[]>(savedHidden);
  const dirty = norm(hidden) !== norm(savedHidden);

  const { trigger, isMutating } = useSWRMutation(
    `/apps/${app.id}/tabs`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
        toast({ title: "Tabs saved" });
      },
      onError: (err: unknown) => toastError(toast, "Save failed", err),
    }
  );

  const toggle = (key: string, shown: boolean) =>
    setHidden((prev) =>
      shown ? prev.filter((k) => k !== key) : [...prev, key]
    );

  return (
    <Section
      title="Tabs"
      action={
        <Button
          size="sm"
          className="h-8"
          disabled={!dirty || isMutating}
          onClick={() => trigger({ hidden: hidden.length ? hidden : null })}
        >
          {isMutating ? "Saving…" : "Save"}
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
        {TAB_OPTIONS.map((t) => (
          <label key={t.key} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={!hidden.includes(t.key)}
              onCheckedChange={(v) => toggle(t.key, v === true)}
            />
            <span>{t.label}</span>
          </label>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Unticked tabs disappear from this app&rsquo;s page for everyone.
        Data stays intact and direct links keep working.
      </p>
    </Section>
  );
};
